import React, { useState, useEffect } from 'react'
import { Button, Modal, Form, Row, Col, Alert } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import {
  saveJitaiDataResp,
  getJitaiDataResp,
  refreshJitaiTasks,
} from '../actions/participantActions'
import {
  SAVE_JITAI_DATA_RESP_RESET,
  GET_JITAI_DATA_RESP_RESET,
  REFRESH_JITAI_TASKS_RESET,
} from '../constants/participantConstants'

export const ScheduleSleepWakeTime = ({ participantId, studyId }) => {
  const [show, setShow] = useState(false)
  const [scheduledSleepTime, setScheduledSleepTime] = useState('')
  const [scheduledWakeTime, setScheduledWakeTime] = useState('')
  const [existingSleepTime, setExistingSleepTime] = useState(null)
  const [existingWakeTime, setExistingWakeTime] = useState(null)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const dispatch = useDispatch()

  const saveJitaiDataRespState = useSelector(
    (state) => state.saveJitaiDataResp
  )
  const {
    loading: loadingSave,
    success: successSave,
    error: errorSave,
  } = saveJitaiDataRespState

  const getJitaiDataRespState = useSelector((state) => state.getJitaiDataResp)
  const {
    loading: loadingGet,
    responses: existingResponses,
  } = getJitaiDataRespState

  const refreshJitaiTasksState = useSelector((state) => state.refreshJitaiTasks)
  const {
    loading: loadingRefresh,
    success: successRefresh,
    error: errorRefresh,
    tasksDeleted,
    tasksCreated,
  } = refreshJitaiTasksState

  // Load existing values when modal opens
  useEffect(() => {
    if (show) {
      // Get all JITAI data responses for this participant (no code filter)
      dispatch(getJitaiDataResp(studyId, participantId))
    }
  }, [show, dispatch, studyId, participantId])

  // Process existing responses
  useEffect(() => {
    if (!show) {
      return
    }
    if (existingResponses && existingResponses.length > 0) {
      const sleepTimeResp = existingResponses.find(
        (r) => r.resp && r.resp.code === 'ScheduledSleepTime'
      )
      const wakeTimeResp = existingResponses.find(
        (r) => r.resp && r.resp.code === 'ScheduledWakeTime'
      )

      if (sleepTimeResp) {
        setExistingSleepTime(sleepTimeResp.response)
      }
      if (wakeTimeResp) {
        setExistingWakeTime(wakeTimeResp.response)
      }
    }
  }, [existingResponses, show])

  // Handle save success - trigger refresh JITAI tasks
  useEffect(() => {
    if (show && successSave) {
      // Reload existing values
      dispatch(getJitaiDataResp(studyId, participantId))
      dispatch({ type: SAVE_JITAI_DATA_RESP_RESET })
      setError('')
      // Clear input fields after successful save
      setScheduledSleepTime('')
      setScheduledWakeTime('')
      // Refresh JITAI tasks after saving sleep/wake time
      dispatch(refreshJitaiTasks(studyId, participantId))
    }
    if (show && errorSave) {
      setError(errorSave)
      dispatch({ type: SAVE_JITAI_DATA_RESP_RESET })
    }
  }, [successSave, errorSave, dispatch, studyId, participantId, show])

  // Handle refresh JITAI tasks result
  useEffect(() => {
    if (!show) {
      return
    }
    if (successRefresh) {
      setSuccessMessage(`JITAI tasks refreshed: ${tasksDeleted} deleted, ${tasksCreated} created`)
      dispatch({ type: REFRESH_JITAI_TASKS_RESET })
    }
    if (errorRefresh) {
      setError(`Failed to refresh JITAI tasks: ${errorRefresh}`)
      dispatch({ type: REFRESH_JITAI_TASKS_RESET })
    }
  }, [successRefresh, errorRefresh, tasksDeleted, tasksCreated, dispatch, show])

  const handleOpen = () => {
    setShow(true)
    setScheduledSleepTime('')
    setScheduledWakeTime('')
    setError('')
    setSuccessMessage('')
    setExistingSleepTime(null)
    setExistingWakeTime(null)
  }

  const handleClose = () => {
    setShow(false)
    setScheduledSleepTime('')
    setScheduledWakeTime('')
    setError('')
    setSuccessMessage('')
    dispatch({ type: SAVE_JITAI_DATA_RESP_RESET })
    dispatch({ type: GET_JITAI_DATA_RESP_RESET })
    dispatch({ type: REFRESH_JITAI_TASKS_RESET })
  }

  // Validate time format: hh:mm AM/PM
  const isValidTimeFormat = (time) => {
    const timeRegex = /^(0[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/
    return timeRegex.test(time)
  }

  const handleSave = () => {
    setError('')

    // Validate both times if provided
    if (scheduledSleepTime && !isValidTimeFormat(scheduledSleepTime)) {
      setError('Scheduled Sleep Time must be in format "hh:mm AM/PM" (e.g., "07:00 AM" or "11:00 PM"). Note: Use leading zero for single-digit hours (e.g., "07" not "7"), there must be a space before AM/PM, and AM/PM must be in capital letters.')
      return
    }

    if (scheduledWakeTime && !isValidTimeFormat(scheduledWakeTime)) {
      setError('Scheduled Wake Time must be in format "hh:mm AM/PM" (e.g., "07:00 AM" or "11:00 PM"). Note: Use leading zero for single-digit hours (e.g., "07" not "7"), there must be a space before AM/PM, and AM/PM must be in capital letters.')
      return
    }

    const responses = []
    
    if (scheduledSleepTime) {
      responses.push({
        code: 'ScheduledSleepTime',
        response: scheduledSleepTime,
      })
    }

    if (scheduledWakeTime) {
      responses.push({
        code: 'ScheduledWakeTime',
        response: scheduledWakeTime,
      })
    }

    if (responses.length === 0) {
      setError('Please provide at least one time value')
      return
    }

    dispatch(saveJitaiDataResp(studyId, participantId, responses))
  }

  return (
    <>
      <Button
        variant='primary'
        className='btn-sm m-1'
        onClick={handleOpen}
      >
        Schedule Sleep/Wake Time
      </Button>

      <Modal show={show} onHide={handleClose} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Schedule Sleep and Wake Time</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant='danger'>{error}</Alert>}
          {successMessage && <Alert variant='success'>{successMessage}</Alert>}
          {loadingRefresh && <Alert variant='info'>Refreshing JITAI tasks...</Alert>}

          <Form>
            <Form.Group as={Row} className='mb-3'>
              <Form.Label column sm={4}>
                Scheduled Sleep Time
              </Form.Label>
              <Col sm={8}>
                {existingSleepTime && (
                  <Alert variant='info' className='mb-2' style={{ fontSize: '0.875rem', padding: '0.5rem' }}>
                    Current saved value: <strong>{existingSleepTime}</strong>
                  </Alert>
                )}
                <Form.Control
                  type='text'
                  placeholder='e.g., 11:00 PM'
                  value={scheduledSleepTime}
                  onChange={(e) => {
                    const value = e.target.value
                    setScheduledSleepTime(value)
                  }}
                  disabled={loadingSave || loadingGet || loadingRefresh}
                />
                <Form.Text className='text-muted'>
                  Format: hh:mm AM/PM (e.g., "07:00 AM" or "11:00 PM")
                </Form.Text>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className='mb-3'>
              <Form.Label column sm={4}>
                Scheduled Wake Time
              </Form.Label>
              <Col sm={8}>
                {existingWakeTime && (
                  <Alert variant='info' className='mb-2' style={{ fontSize: '0.875rem', padding: '0.5rem' }}>
                    Current saved value: <strong>{existingWakeTime}</strong>
                  </Alert>
                )}
                <Form.Control
                  type='text'
                  placeholder='e.g., 07:00 AM'
                  value={scheduledWakeTime}
                  onChange={(e) => {
                    const value = e.target.value
                    setScheduledWakeTime(value)
                  }}
                  disabled={loadingSave || loadingGet || loadingRefresh}
                />
                <Form.Text className='text-muted'>
                  Format: hh:mm AM/PM (e.g., "07:00 AM" or "11:00 PM")
                </Form.Text>
              </Col>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose} disabled={loadingSave || loadingRefresh}>
            Close
          </Button>
          <Button
            variant='primary'
            onClick={handleSave}
            disabled={loadingSave || loadingGet || loadingRefresh}
          >
            {loadingSave ? 'Saving...' : loadingRefresh ? 'Refreshing tasks...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ScheduleSleepWakeTime

